import type { AgentsListResult, GatewaySessionRow } from "../types.ts";
import { isSubagentSessionKey, parseAgentSessionKey } from "../../../../src/routing/session-key.js";

export type ChatTargetOption = {
  sessionKey: string;
  label: string;
  group: "main" | "employee" | "agent";
};

type EmployeeRow = {
  agentId: string;
  employee: { name: string; role?: string };
};

type AgentRow = { id: string; name?: string; identity?: { name?: string } };

export function buildEmployeeSessionKey(agentId: string): string {
  return resolveAgentMainSessionKey(agentId, []);
}

export function resolveAgentMainSessionKey(agentId: string, sessions: GatewaySessionRow[]): string {
  const found = sessions.find((row) => {
    const parsed = parseAgentSessionKey(row.key);
    return parsed?.agentId === agentId && parsed?.rest === "main";
  });
  if (found) {
    return found.key;
  }
  return `agent:${agentId}:main`;
}

export function buildChatTargetOptions(params: {
  mainSessionKey: string | null;
  sessionKey: string;
  employees: EmployeeRow[];
  agents: AgentRow[];
  sessions: GatewaySessionRow[];
}): ChatTargetOption[] {
  const options: ChatTargetOption[] = [];
  const seen = new Set<string>();

  const add = (sessionKey: string, label: string, group: ChatTargetOption["group"]) => {
    const key = sessionKey.trim();
    if (!key || seen.has(key) || isSubagentSessionKey(key)) {
      return;
    }
    seen.add(key);
    options.push({ sessionKey: key, label, group });
  };

  const registryIds = new Set(params.agents.map((agent) => agent.id));

  if (params.mainSessionKey) {
    add(params.mainSessionKey, "Main session", "main");
  }

  // Primary source: same `agents.list` rows as the Agents page.
  for (const agent of params.agents) {
    const key = resolveAgentMainSessionKey(agent.id, params.sessions);
    const name = agent.identity?.name?.trim() || agent.name?.trim() || agent.id;
    add(key, name, "agent");
  }

  // Employees only when they map to an agent id not in agents.list (avoid duplicate / mismatched labels).
  for (const emp of params.employees) {
    if (registryIds.has(emp.agentId)) {
      continue;
    }
    const key = resolveAgentMainSessionKey(emp.agentId, params.sessions);
    const role = emp.employee.role?.trim();
    const label = role ? `${emp.employee.name} (${role})` : emp.employee.name;
    add(key, label, "employee");
  }

  if (!seen.has(params.sessionKey)) {
    const row = params.sessions.find((s) => s.key === params.sessionKey);
    const label = row?.label?.trim() || row?.displayName?.trim() || params.sessionKey;
    add(
      params.sessionKey,
      isSubagentSessionKey(params.sessionKey) ? `${label} (subagent)` : label,
      "main",
    );
  }

  return options;
}

export function resolveSelectedTargetValue(
  sessionKey: string,
  options: ChatTargetOption[],
): string {
  if (options.some((opt) => opt.sessionKey === sessionKey)) {
    return sessionKey;
  }
  const parsed = parseAgentSessionKey(sessionKey);
  if (parsed?.rest === "main") {
    const match = options.find((opt) => {
      const other = parseAgentSessionKey(opt.sessionKey);
      return other?.agentId === parsed.agentId && other?.rest === "main";
    });
    if (match) {
      return match.sessionKey;
    }
  }
  return sessionKey;
}
